/**
 * Error definitions and error codes for Bifrost RPC Framework
 */
/**
 * Error code ranges
 */
var ErrorCategory;
(function (ErrorCategory) {
    ErrorCategory[ErrorCategory["TRANSPORT"] = 1000] = "TRANSPORT";
    ErrorCategory[ErrorCategory["PROTOCOL"] = 2000] = "PROTOCOL";
    ErrorCategory[ErrorCategory["SERVICE"] = 3000] = "SERVICE";
    ErrorCategory[ErrorCategory["APPLICATION"] = 4000] = "APPLICATION";
    ErrorCategory[ErrorCategory["INTERNAL"] = 5000] = "INTERNAL";
})(ErrorCategory || (ErrorCategory = {}));
/**
 * Standard error codes
 */
var ErrorCode;
(function (ErrorCode) {
    // Transport errors (1000-1999)
    ErrorCode[ErrorCode["CONNECTION_FAILED"] = 1001] = "CONNECTION_FAILED";
    ErrorCode[ErrorCode["CONNECTION_TIMEOUT"] = 1002] = "CONNECTION_TIMEOUT";
    ErrorCode[ErrorCode["MESSAGE_TOO_LARGE"] = 1003] = "MESSAGE_TOO_LARGE";
    ErrorCode[ErrorCode["TOO_MANY_INFLIGHT"] = 1004] = "TOO_MANY_INFLIGHT";
    // Protocol errors (2000-2999)
    ErrorCode[ErrorCode["INVALID_REQUEST"] = 2001] = "INVALID_REQUEST";
    ErrorCode[ErrorCode["METHOD_NOT_FOUND"] = 2002] = "METHOD_NOT_FOUND";
    ErrorCode[ErrorCode["INVALID_PARAMS"] = 2003] = "INVALID_PARAMS";
    // Service errors (3000-3999)
    ErrorCode[ErrorCode["SERVICE_NOT_FOUND"] = 3001] = "SERVICE_NOT_FOUND";
    ErrorCode[ErrorCode["SERVICE_ERROR"] = 3002] = "SERVICE_ERROR";
    ErrorCode[ErrorCode["SERVICE_TIMEOUT"] = 3003] = "SERVICE_TIMEOUT";
    // Application errors (4000-4999)
    ErrorCode[ErrorCode["UNAUTHORIZED"] = 4001] = "UNAUTHORIZED";
    ErrorCode[ErrorCode["FORBIDDEN"] = 4002] = "FORBIDDEN";
    ErrorCode[ErrorCode["NOT_FOUND"] = 4003] = "NOT_FOUND";
    ErrorCode[ErrorCode["CONFLICT"] = 4004] = "CONFLICT";
    ErrorCode[ErrorCode["VALIDATION_ERROR"] = 4005] = "VALIDATION_ERROR";
    // Internal errors (5000-5999)
    ErrorCode[ErrorCode["INTERNAL_ERROR"] = 5000] = "INTERNAL_ERROR";
    ErrorCode[ErrorCode["NOT_IMPLEMENTED"] = 5001] = "NOT_IMPLEMENTED";
    ErrorCode[ErrorCode["NETWORK_ERROR"] = 1005] = "NETWORK_ERROR";
    ErrorCode[ErrorCode["TIMEOUT_ERROR"] = 1006] = "TIMEOUT_ERROR";
    ErrorCode[ErrorCode["SERVICE_UNAVAILABLE"] = 3004] = "SERVICE_UNAVAILABLE";
})(ErrorCode || (ErrorCode = {}));
/**
 * Custom RPC call error
 */
class RpcCallError extends Error {
    constructor(rpcError) {
        super(rpcError.message);
        this.rpcError = rpcError;
        this.name = "RpcCallError";
        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, RpcCallError);
        }
    }
    get code() {
        return this.rpcError.code;
    }
    get data() {
        return this.rpcError.data;
    }
}

/**
 * Connection state management for Bifrost
 */
class ConnectionState {
    constructor(timeout) {
        this.timeout = timeout;
        this.ready = false;
        this.readyPromise = new Promise((resolve) => {
            this.readyResolve = resolve;
        });
    }
    /**
     * Ensure connection is ready, with timeout
     */
    async ensureReady() {
        if (this.ready) {
            return Promise.resolve();
        }
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Connection timeout")), this.timeout);
        });
        return Promise.race([this.readyPromise, timeoutPromise]);
    }
    /**
     * Mark connection as ready
     */
    setReady() {
        if (!this.ready) {
            this.ready = true;
            this.readyResolve();
        }
    }
    /**
     * Check if connection is ready
     */
    isReady() {
        return this.ready;
    }
    /**
     * Reset connection state (for testing/reconnection)
     */
    reset() {
        this.ready = false;
        this.readyPromise = new Promise((resolve) => {
            this.readyResolve = resolve;
        });
    }
}

/**
 * Retry logic manager for Bifrost operations
 */
class RetryManager {
    constructor(config) {
        this.config = {
            maxRetries: config.maxRetries ?? 3,
            retryDelay: config.retryDelay ?? 1000,
            retryBackoff: config.retryBackoff ?? 2,
            retryableErrors: config.retryableErrors ?? [
                ErrorCode.CONNECTION_FAILED,
                ErrorCode.CONNECTION_TIMEOUT,
                ErrorCode.INTERNAL_ERROR,
            ],
        };
    }
    /**
     * Execute operation with retry logic
     */
    async withRetry(operation) {
        let lastError;
        let attempt = 0;
        while (attempt <= this.config.maxRetries) {
            try {
                return await operation();
            }
            catch (error) {
                lastError = error;
                // Don't retry on last attempt or if error is not retryable
                if (attempt === this.config.maxRetries ||
                    !this.isRetryableError(error)) {
                    throw error;
                }
                // Calculate delay with exponential backoff
                const delay = this.calculateDelay(attempt);
                await this.sleep(delay);
                attempt++;
            }
        }
        throw lastError;
    }
    /**
     * Check if error is retryable
     */
    isRetryableError(error) {
        if (error instanceof RpcCallError) {
            return this.config.retryableErrors.includes(error.code);
        }
        // Generic errors are not retryable by default
        return false;
    }
    /**
     * Calculate delay with exponential backoff
     */
    calculateDelay(attempt) {
        return this.config.retryDelay * Math.pow(this.config.retryBackoff, attempt);
    }
    /**
     * Sleep utility
     */
    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    /**
     * Get current retry configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Check if specific error code is retryable
     */
    isErrorCodeRetryable(code) {
        return this.config.retryableErrors.includes(code);
    }
}

/**
 * Service validation utilities
 */
class ServiceValidator {
    /**
     * Validate service implementation
     */
    static validateService(name, implementation) {
        this.validateServiceName(name);
        this.validateServiceImplementation(implementation);
        this.validateServiceMethods(implementation);
    }
    /**
     * Validate service name format
     */
    static validateServiceName(name) {
        if (!name || name.trim() === "") {
            throw new Error("Service name cannot be empty");
        }
        if (!this.SERVICE_NAME_REGEX.test(name)) {
            if (/^[0-9]/.test(name)) {
                throw new Error("Service name must start with a letter");
            }
            throw new Error("Service name contains invalid characters");
        }
    }
    /**
     * Validate service implementation object
     */
    static validateServiceImplementation(implementation) {
        if (implementation === null || implementation === undefined) {
            throw new Error("Service implementation cannot be null or undefined");
        }
        if (typeof implementation !== "object" || Array.isArray(implementation)) {
            throw new Error("Service implementation must be an object");
        }
    }
    /**
     * Validate service methods
     */
    static validateServiceMethods(implementation) {
        const methods = this.getServiceMethods(implementation);
        if (methods.length === 0) {
            throw new Error("Service must have at least one method");
        }
        // Validate each method
        for (const methodName of methods) {
            this.validateMethodName(methodName);
            this.validateMethodSignature(implementation[methodName], methodName);
        }
    }
    /**
     * Validate method name
     */
    static validateMethodName(methodName) {
        if (this.RESERVED_METHOD_NAMES.includes(methodName)) {
            throw new Error(`Method name is reserved: ${methodName}`);
        }
        // Method names should follow similar rules as service names
        if (!this.SERVICE_NAME_REGEX.test(methodName)) {
            throw new Error(`Invalid method name: ${methodName}`);
        }
    }
    /**
     * Validate method signature
     */
    static validateMethodSignature(method, methodName) {
        const paramCount = method.length;
        // Methods should accept 0-2 parameters: (payload?, context?)
        if (paramCount > 2) {
            throw new Error(`Method ${methodName} has too many parameters (expected 0-2, got ${paramCount})`);
        }
    }
    /**
     * Get list of method names from service
     */
    static getServiceMethods(implementation) {
        const methods = [];
        for (const prop in implementation) {
            if (typeof implementation[prop] === "function") {
                methods.push(prop);
            }
        }
        return methods.sort(); // Return sorted for consistent ordering
    }
    /**
     * Check if a method name is reserved
     */
    static isReservedMethodName(methodName) {
        return this.RESERVED_METHOD_NAMES.includes(methodName);
    }
    /**
     * Validate service name format (public method)
     */
    static isValidServiceName(name) {
        try {
            this.validateServiceName(name);
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Get validation errors without throwing
     */
    static getValidationErrors(name, implementation) {
        const errors = [];
        try {
            this.validateServiceName(name);
        }
        catch (error) {
            errors.push(error.message);
        }
        try {
            this.validateServiceImplementation(implementation);
        }
        catch (error) {
            errors.push(error.message);
            return errors; // Can't continue without valid implementation
        }
        try {
            this.validateServiceMethods(implementation);
        }
        catch (error) {
            errors.push(error.message);
        }
        return errors;
    }
}
ServiceValidator.RESERVED_METHOD_NAMES = [
    "constructor",
    "prototype",
    "toString",
    "valueOf",
    "hasOwnProperty",
    "isPrototypeOf",
    "propertyIsEnumerable",
    "__proto__",
    "__defineGetter__",
    "__defineSetter__",
    "__lookupGetter__",
    "__lookupSetter__",
];
ServiceValidator.SERVICE_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Service registry for managing RPC services
 */
class ServiceRegistry {
    constructor() {
        this.services = new Map();
    }
    /**
     * Register a service implementation
     */
    register(name, implementation) {
        if (this.services.has(name)) {
            throw new Error(`Service already registered: ${name}`);
        }
        // Use ServiceValidator for comprehensive validation
        ServiceValidator.validateService(name, implementation);
        this.services.set(name, implementation);
    }
    /**
     * Unregister a service
     */
    unregister(name) {
        this.services.delete(name);
    }
    /**
     * Invoke a service method
     */
    async invoke(service, method, payload) {
        const impl = this.services.get(service);
        if (!impl) {
            throw new RpcCallError({
                code: ErrorCode.SERVICE_NOT_FOUND,
                message: `Service not found: ${service}`,
            });
        }
        const fn = impl[method];
        if (typeof fn !== "function") {
            throw new RpcCallError({
                code: ErrorCode.METHOD_NOT_FOUND,
                message: `Method not found: ${service}.${method}`,
            });
        }
        try {
            // Create context for service call
            const context = {};
            // Call method with payload and context
            const result = await fn.call(impl, payload, context);
            return result;
        }
        catch (error) {
            // Re-throw RpcCallError as-is
            if (error instanceof RpcCallError) {
                throw error;
            }
            // Wrap other errors in RpcCallError
            throw new RpcCallError({
                code: ErrorCode.SERVICE_ERROR,
                message: error instanceof Error ? error.message : "Unknown service error",
                data: error,
            });
        }
    }
    /**
     * List all registered service names
     */
    listServices() {
        return Array.from(this.services.keys());
    }
    /**
     * Get service descriptor with method information
     */
    getServiceDescriptor(serviceName) {
        const impl = this.services.get(serviceName);
        if (!impl) {
            throw new Error(`Service not found: ${serviceName}`);
        }
        const methodNames = ServiceValidator.getServiceMethods(impl);
        const methods = methodNames.map((name) => ({
            name,
            // TODO: Add parameter introspection in future version
            params: [],
            returns: "any",
        }));
        return {
            name: serviceName,
            methods,
        };
    }
    /**
     * Check if service has a specific method
     */
    hasMethod(serviceName, methodName) {
        const impl = this.services.get(serviceName);
        return impl && typeof impl[methodName] === "function";
    }
    /**
     * Clear all registered services (useful for testing)
     */
    clear() {
        this.services.clear();
    }
    /**
     * Get the number of registered services
     */
    size() {
        return this.services.size;
    }
}

/**
 * InterceptorChain - Manages and executes interceptors in order
 */
class InterceptorChain {
    constructor() {
        this.interceptors = [];
    }
    /**
     * Add an interceptor to the chain
     */
    addInterceptor(interceptor) {
        // Remove existing interceptor with same name
        this.removeInterceptor(interceptor.name);
        // Add new interceptor and sort by order
        this.interceptors.push(interceptor);
        this.interceptors.sort((a, b) => a.order - b.order);
    }
    /**
     * Remove interceptor by name
     */
    removeInterceptor(name) {
        this.interceptors = this.interceptors.filter((i) => i.name !== name);
    }
    /**
     * Clear all interceptors
     */
    clearInterceptors() {
        this.interceptors = [];
    }
    /**
     * Get all interceptors
     */
    getInterceptors() {
        return [...this.interceptors];
    }
    /**
     * Process a message through the interceptor chain
     */
    async process(direction, message, finalHandler) {
        if (this.interceptors.length === 0) {
            return finalHandler();
        }
        let currentIndex = 0;
        const next = async () => {
            if (currentIndex >= this.interceptors.length) {
                return finalHandler();
            }
            const interceptor = this.interceptors[currentIndex++];
            return interceptor.intercept(direction, message, next);
        };
        return next();
    }
}

/**
 * NativeProxyManager - Advanced proxy creation with caching and validation
 */
/**
 * Enhanced native proxy manager with caching, validation, and TypeScript type safety
 */
class NativeProxyManager {
    constructor(bifrostWeb) {
        this.bifrostWeb = bifrostWeb;
        this.proxyCache = new Map();
        this.proxyMetadata = new Map();
        this.isDestroyed = false;
    }
    /**
     * Create a typed native proxy with caching and validation
     */
    createProxy(serviceName, options = {}) {
        this.throwIfDestroyed();
        this.validateServiceName(serviceName);
        // Return cached proxy if it exists
        if (this.proxyCache.has(serviceName)) {
            return this.proxyCache.get(serviceName);
        }
        // Create new proxy with enhanced features
        const proxy = this.createEnhancedProxy(serviceName, options);
        // Cache the proxy
        this.proxyCache.set(serviceName, proxy);
        // Store metadata
        this.proxyMetadata.set(serviceName, {
            serviceName,
            createdAt: new Date(),
            callCount: 0,
            options: { ...options },
            defaultCallOptions: options.defaultCallOptions,
            methodOptions: options.methodOptions,
        });
        return proxy;
    }
    /**
     * Get metadata for a cached proxy
     */
    getProxyMetadata(serviceName) {
        return this.proxyMetadata.get(serviceName);
    }
    /**
     * List all cached proxy service names
     */
    listCachedProxies() {
        return Array.from(this.proxyCache.keys());
    }
    /**
     * Clear specific proxy from cache
     */
    clearProxy(serviceName) {
        this.proxyCache.delete(serviceName);
        this.proxyMetadata.delete(serviceName);
    }
    /**
     * Clear all proxies from cache
     */
    clearAllProxies() {
        this.proxyCache.clear();
        this.proxyMetadata.clear();
    }
    /**
     * Destroy the proxy manager and cleanup resources
     */
    destroy() {
        this.clearAllProxies();
        this.isDestroyed = true;
    }
    /**
     * Create enhanced proxy with advanced features
     */
    createEnhancedProxy(serviceName, options) {
        const handler = {
            get: (_target, prop) => {
                if (typeof prop !== "string") {
                    return undefined;
                }
                return (...args) => {
                    // Get metadata and increment call count
                    const metadata = this.proxyMetadata.get(serviceName);
                    if (metadata) {
                        metadata.callCount++;
                    }
                    // Extract options from last parameter if present
                    let callOptions;
                    let methodArgs = args;
                    // Check if last argument looks like options
                    if (args.length > 0 &&
                        this.isOptionsParameter(args[args.length - 1])) {
                        callOptions = args[args.length - 1];
                        methodArgs = args.slice(0, -1);
                    }
                    // Determine payload - single arg becomes the payload, multiple args become array
                    let payload;
                    if (methodArgs.length === 0) {
                        payload = undefined;
                    }
                    else if (methodArgs.length === 1) {
                        payload = methodArgs[0];
                    }
                    else {
                        payload = methodArgs;
                    }
                    // Validate calls if enabled
                    if (options.validateCalls) {
                        this.validateMethodCall(serviceName, prop, payload);
                    }
                    // Get proxy-level default options from metadata
                    const proxyCallOptions = metadata?.defaultCallOptions;
                    // Get method-specific options from metadata
                    const methodCallOptions = metadata?.methodOptions?.[prop];
                    // Merge options: global → proxy → method → call
                    // BifrostWeb.mergeCallOptions handles global + proxy + method + call merging
                    const mergedOptions = this.bifrostWeb.mergeCallOptions(proxyCallOptions, methodCallOptions, callOptions);
                    // Determine if this is a notification (void method) or call (promise method)
                    // For now, we'll treat all methods as calls unless specifically configured
                    // In a real implementation, this could be inferred from TypeScript types
                    if (this.isNotificationMethod(prop, options)) {
                        this.bifrostWeb.notify(serviceName, prop, payload);
                        return; // void return for notifications
                    }
                    else {
                        // Return promise for calls with merged options
                        return this.bifrostWeb.call(serviceName, prop, payload, mergedOptions);
                    }
                };
            },
        };
        // Create proxy with empty target object
        return new Proxy({}, handler);
    }
    /**
     * Validate service name format
     */
    validateServiceName(serviceName) {
        if (!serviceName || typeof serviceName !== "string") {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
        // Service name must start with letter and contain only letters, numbers, underscore
        const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
        if (!validNamePattern.test(serviceName)) {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
    }
    /**
     * Check if parameter looks like call options
     */
    isOptionsParameter(param) {
        return (param &&
            typeof param === "object" &&
            !Array.isArray(param) &&
            (param.timeout !== undefined ||
                param.retryConfig !== undefined ||
                param.binary !== undefined));
    }
    /**
     * Validate method call (placeholder for enhanced validation)
     */
    validateMethodCall(serviceName, method, _payload) {
        // Basic validation - could be enhanced with schema validation
        if (!method || typeof method !== "string") {
            throw new Error(`Invalid method name for service ${serviceName}: ${method}`);
        }
        // Additional validations could include:
        // - Parameter type checking
        // - Schema validation
        // - Service method existence checking
    }
    /**
     * Determine if method should be treated as notification
     * This is a placeholder - in practice could use TypeScript reflection or configuration
     */
    isNotificationMethod(method, _options) {
        // Simple heuristic: methods named 'notify' or ending in 'Notify' are notifications
        return (method === "notify" ||
            method.endsWith("Notify") ||
            method.startsWith("notify"));
    }
    /**
     * Throw error if proxy manager has been destroyed
     */
    throwIfDestroyed() {
        if (this.isDestroyed) {
            throw new Error("NativeProxyManager has been destroyed");
        }
    }
}

/**
 * Built-in request and response interceptors
 */
/**
 * Request interceptor for modifying outbound requests
 */
class RequestInterceptor {
    constructor() {
        this.name = "RequestInterceptor";
        this.order = 100;
        this.headers = {};
    }
    /**
     * Add a header to all outbound requests
     */
    addRequestHeader(key, value) {
        this.headers[key] = value;
    }
    /**
     * Remove a header from outbound requests
     */
    removeRequestHeader(key) {
        delete this.headers[key];
    }
    async intercept(direction, message, next) {
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            // Add headers to outbound requests
            if (Object.keys(this.headers).length > 0) {
                message.headers = {
                    ...message.headers,
                    ...this.headers,
                };
            }
        }
        return next();
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
}
/**
 * Response interceptor for modifying inbound responses
 */
class ResponseInterceptor {
    constructor() {
        this.name = "ResponseInterceptor";
        this.order = 200;
        this.responseTransforms = [];
    }
    /**
     * Add a response transformation function
     */
    addResponseTransform(transform) {
        this.responseTransforms.push(transform);
    }
    /**
     * Clear all response transformations
     */
    clearResponseTransforms() {
        this.responseTransforms = [];
    }
    async intercept(direction, message, next) {
        const result = await next();
        if (direction === "inbound" && this.isRpcResponse(message)) {
            // Apply response transformations
            let transformedResponse = message;
            for (const transform of this.responseTransforms) {
                transformedResponse = transform(transformedResponse);
            }
            return transformedResponse;
        }
        return result;
    }
    isRpcResponse(message) {
        return "ok" in message;
    }
}
/**
 * Timing interceptor for measuring request performance
 */
class TimingInterceptor {
    constructor() {
        this.name = "TimingInterceptor";
        this.order = 10; // Run early to capture full timing
    }
    async intercept(direction, message, next) {
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            const startTime = performance.now();
            const result = await next();
            const endTime = performance.now();
            const duration = endTime - startTime;
            // Add timing data to the message
            message.timing = {
                startTime,
                endTime,
                duration,
            };
            return result;
        }
        return next();
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
}
/**
 * Validation interceptor for ensuring message integrity
 */
class ValidationInterceptor {
    constructor() {
        this.name = "ValidationInterceptor";
        this.order = 1; // Run first to catch issues early
        this.customRules = {
            inbound: [],
            outbound: [],
        };
    }
    /**
     * Add a custom validation rule
     */
    addValidationRule(direction, rule) {
        this.customRules[direction].push(rule);
    }
    /**
     * Clear all validation rules for a direction
     */
    clearValidationRules(direction) {
        this.customRules[direction] = [];
    }
    async intercept(direction, message, next) {
        // Validate message structure
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            this.validateOutboundRequest(message);
        }
        else if (direction === "inbound" && this.isRpcResponse(message)) {
            this.validateInboundResponse(message);
        }
        // Apply custom validation rules
        for (const rule of this.customRules[direction]) {
            rule(message);
        }
        return next();
    }
    validateOutboundRequest(envelope) {
        if (!envelope.id) {
            throw new Error("Invalid request: missing id");
        }
        if (!envelope.kind) {
            throw new Error("Invalid request: missing kind");
        }
        if (envelope.kind === "call" && !envelope.service) {
            throw new Error("Invalid request: missing service");
        }
        if (envelope.kind === "call" && !envelope.method) {
            throw new Error("Invalid request: missing method");
        }
        if (!envelope.ts) {
            throw new Error("Invalid request: missing timestamp");
        }
    }
    validateInboundResponse(response) {
        if (!response.id) {
            throw new Error("Invalid response: missing id");
        }
        if (typeof response.ok !== "boolean") {
            throw new Error("Invalid response: missing ok field");
        }
        if (!response.ts) {
            throw new Error("Invalid response: missing timestamp");
        }
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
    isRpcResponse(message) {
        return !("kind" in message);
    }
}

/**
 * Logging interceptor for debugging and monitoring RPC calls
 */
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["NONE"] = 4] = "NONE";
})(LogLevel || (LogLevel = {}));
class LoggingInterceptor {
    constructor() {
        this.name = "LoggingInterceptor";
        this.order = 1000; // Run last to see final state
        this.logLevel = LogLevel.INFO;
        this.enabled = true;
        this.sensitiveFields = [];
        this.logger = console;
    }
    /**
     * Set the minimum log level
     */
    setLogLevel(level) {
        this.logLevel = level;
    }
    /**
     * Enable or disable logging
     */
    setEnabled(enabled) {
        this.enabled = enabled;
    }
    /**
     * Set fields that should be redacted in logs
     */
    setSensitiveFields(fields) {
        this.sensitiveFields = fields;
    }
    /**
     * Set a custom logger implementation
     */
    setCustomLogger(logger) {
        this.logger = logger;
    }
    async intercept(direction, message, next) {
        if (!this.enabled) {
            return next();
        }
        // Log the message before processing
        this.logMessage(direction, message);
        return next();
    }
    logMessage(direction, message) {
        if (this.isRpcEnvelope(message)) {
            this.logRequest(direction, message);
        }
        else {
            this.logResponse(direction, message);
        }
    }
    logRequest(direction, envelope) {
        const logData = this.sanitizeMessage(envelope);
        if (envelope.kind === "notify") {
            this.log(LogLevel.INFO, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC notification:`, logData);
        }
        else {
            this.log(LogLevel.INFO, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC call:`, logData);
        }
    }
    logResponse(direction, response) {
        const logData = this.sanitizeMessage(response);
        if (response.error) {
            this.log(LogLevel.ERROR, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC error:`, logData);
        }
        else {
            this.log(LogLevel.INFO, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC response:`, logData);
        }
    }
    sanitizeMessage(message) {
        if (this.sensitiveFields.length === 0) {
            return message;
        }
        const sanitized = { ...message };
        for (const field of this.sensitiveFields) {
            if (field in sanitized) {
                sanitized[field] = "[REDACTED]";
            }
        }
        return sanitized;
    }
    log(level, message, ...args) {
        if (level < this.logLevel) {
            return;
        }
        switch (level) {
            case LogLevel.DEBUG:
                this.logger.debug(message, ...args);
                break;
            case LogLevel.INFO:
                this.logger.info(message, ...args);
                break;
            case LogLevel.WARN:
                this.logger.warn(message, ...args);
                break;
            case LogLevel.ERROR:
                this.logger.error(message, ...args);
                break;
        }
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
}

/**
 * Authentication interceptor for securing RPC calls
 */
class AuthInterceptor {
    constructor(authProvider) {
        this.name = "AuthInterceptor";
        this.order = 50; // Run after validation but before logging
        this.requiredAuthServices = new Set();
        this.excludedServices = new Set();
        this.requiredAuthMethods = new Map();
        this.authHeaderFormat = (token) => `Bearer ${token}`;
        this.authProvider = authProvider;
    }
    /**
     * Require authentication for a specific service
     */
    requireAuthForService(serviceName) {
        this.requiredAuthServices.add(serviceName);
    }
    /**
     * Exclude a service from authentication
     */
    excludeService(serviceName) {
        this.excludedServices.add(serviceName);
    }
    /**
     * Require authentication for a specific method
     */
    requireAuthForMethod(serviceName, methodName) {
        if (!this.requiredAuthMethods.has(serviceName)) {
            this.requiredAuthMethods.set(serviceName, new Set());
        }
        this.requiredAuthMethods.get(serviceName).add(methodName);
    }
    /**
     * Set custom auth header format
     */
    setAuthHeaderFormat(formatter) {
        this.authHeaderFormat = formatter;
    }
    async intercept(direction, message, next) {
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            await this.handleOutboundRequest(message);
        }
        else if (direction === "inbound" && this.isRpcResponse(message)) {
            this.handleInboundResponse(message);
        }
        return next();
    }
    async handleOutboundRequest(envelope) {
        // Skip notifications
        if (envelope.kind === "notify") {
            return;
        }
        // Skip excluded services
        if (envelope.service && this.excludedServices.has(envelope.service)) {
            return;
        }
        // Check if auth is required
        const requiresAuth = this.requiresAuthentication(envelope);
        // Get token
        let token = await this.authProvider.getToken();
        // If we have a token, validate it
        if (token && !this.authProvider.isTokenValid(token)) {
            token = await this.authProvider.refreshToken();
        }
        // Check if token is required but not available
        if (requiresAuth && !token) {
            if (envelope.method &&
                this.requiredAuthMethods.get(envelope.service)?.has(envelope.method)) {
                throw new Error(`Authentication required for method: ${envelope.service}.${envelope.method}`);
            }
            else {
                throw new Error(`Authentication required for service: ${envelope.service}`);
            }
        }
        // Add auth header if token is available
        if (token) {
            if (!envelope.headers) {
                envelope.headers = {};
            }
            envelope.headers["Authorization"] = this.authHeaderFormat(token);
        }
    }
    handleInboundResponse(response) {
        // Check for auth-related errors
        if (response.error) {
            const isAuthError = response.error.code === ErrorCode.UNAUTHORIZED ||
                response.error.code === ErrorCode.FORBIDDEN ||
                (response.error.code >= 4001 && response.error.code <= 4099);
            if (isAuthError) {
                this.authProvider.onTokenExpired();
            }
        }
    }
    requiresAuthentication(envelope) {
        if (!envelope.service) {
            return false;
        }
        // Check if service requires auth
        if (this.requiredAuthServices.has(envelope.service)) {
            return true;
        }
        // Check if specific method requires auth
        if (envelope.method) {
            const serviceMethods = this.requiredAuthMethods.get(envelope.service);
            if (serviceMethods && serviceMethods.has(envelope.method)) {
                return true;
            }
        }
        return false;
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
    isRpcResponse(message) {
        return "ok" in message;
    }
}

/**
 * Circuit breaker interceptor for fault tolerance
 */
var CircuitBreakerState;
(function (CircuitBreakerState) {
    CircuitBreakerState["CLOSED"] = "closed";
    CircuitBreakerState["OPEN"] = "open";
    CircuitBreakerState["HALF_OPEN"] = "half_open";
})(CircuitBreakerState || (CircuitBreakerState = {}));
class CircuitBreaker {
    constructor(config, onStateChange) {
        this.config = config;
        this.onStateChange = onStateChange;
        this.state = CircuitBreakerState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.lastFailureTime = null;
        this.openedTime = null;
        this.recoveryTimer = null;
    }
    async execute(operation, failureCondition) {
        if (this.state === CircuitBreakerState.OPEN) {
            throw new Error("Circuit breaker is open");
        }
        try {
            const result = await operation();
            this.onSuccess();
            return result;
        }
        catch (error) {
            if (failureCondition(error)) {
                this.onFailure();
            }
            throw error;
        }
    }
    onSuccess() {
        this.failureCount = 0;
        this.successCount++;
        if (this.state === CircuitBreakerState.HALF_OPEN) {
            this.setState(CircuitBreakerState.CLOSED);
        }
    }
    onFailure() {
        this.failureCount++;
        this.lastFailureTime = Date.now();
        if (this.state === CircuitBreakerState.HALF_OPEN) {
            this.setState(CircuitBreakerState.OPEN);
        }
        else if (this.failureCount >= this.config.failureThreshold) {
            this.setState(CircuitBreakerState.OPEN);
        }
    }
    setState(newState) {
        const oldState = this.state;
        this.state = newState;
        if (newState === CircuitBreakerState.OPEN) {
            this.openedTime = Date.now();
            this.scheduleRecovery();
        }
        if (oldState !== newState && this.onStateChange) {
            this.onStateChange(newState);
        }
    }
    scheduleRecovery() {
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
        }
        this.recoveryTimer = setTimeout(() => {
            this.setState(CircuitBreakerState.HALF_OPEN);
        }, this.config.recoveryTimeout);
    }
    getState() {
        return this.state;
    }
    getMetrics() {
        return {
            state: this.state,
            failureCount: this.failureCount,
            successCount: this.successCount,
            lastFailureTime: this.lastFailureTime,
            openedTime: this.openedTime,
        };
    }
    reset() {
        this.state = CircuitBreakerState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.lastFailureTime = null;
        this.openedTime = null;
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
            this.recoveryTimer = null;
        }
    }
    destroy() {
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
        }
    }
}
class CircuitBreakerInterceptor {
    constructor(config) {
        this.name = "CircuitBreakerInterceptor";
        this.order = 30; // Run after validation but before auth
        this.defaultConfig = {
            failureThreshold: 5,
            recoveryTimeout: 30000,
            monitoringPeriod: 60000,
        };
        this.circuitBreakers = new Map();
        this.serviceConfigs = new Map();
        this.failureCondition = () => true;
        if (config) {
            this.defaultConfig = { ...this.defaultConfig, ...config };
        }
    }
    /**
     * Configure circuit breaker for a specific service
     */
    configureService(serviceName, config) {
        this.serviceConfigs.set(serviceName, { ...this.defaultConfig, ...config });
    }
    /**
     * Set custom failure condition
     */
    setFailureCondition(condition) {
        this.failureCondition = condition;
    }
    async intercept(direction, message, next) {
        // Only apply to outbound RPC calls
        if (direction !== "outbound" || !this.isRpcCall(message)) {
            return next();
        }
        const serviceName = message.service || "default";
        const circuitBreaker = this.getCircuitBreaker(serviceName);
        return circuitBreaker.execute(next, this.failureCondition);
    }
    getCircuitBreaker(serviceName) {
        if (!this.circuitBreakers.has(serviceName)) {
            const config = this.serviceConfigs.get(serviceName) || this.defaultConfig;
            const circuitBreaker = new CircuitBreaker(config);
            this.circuitBreakers.set(serviceName, circuitBreaker);
        }
        return this.circuitBreakers.get(serviceName);
    }
    isRpcCall(message) {
        return "kind" in message && message.kind === "call";
    }
    /**
     * Get current state of circuit breaker
     */
    getState(serviceName = "default") {
        const circuitBreaker = this.circuitBreakers.get(serviceName);
        return circuitBreaker
            ? circuitBreaker.getState()
            : CircuitBreakerState.CLOSED;
    }
    /**
     * Get metrics for all circuit breakers
     */
    getMetrics() {
        const metrics = {};
        // Always include default metrics
        if (!this.circuitBreakers.has("default")) {
            metrics.default = {
                state: CircuitBreakerState.CLOSED,
                failureCount: 0,
                successCount: 0,
                lastFailureTime: null,
                openedTime: null,
            };
        }
        for (const [serviceName, circuitBreaker] of this.circuitBreakers) {
            metrics[serviceName] = circuitBreaker.getMetrics();
        }
        return metrics;
    }
    /**
     * Reset all circuit breakers
     */
    reset(serviceName) {
        if (serviceName) {
            const circuitBreaker = this.circuitBreakers.get(serviceName);
            if (circuitBreaker) {
                circuitBreaker.reset();
            }
        }
        else {
            for (const circuitBreaker of this.circuitBreakers.values()) {
                circuitBreaker.reset();
            }
        }
    }
    /**
     * Cleanup resources
     */
    destroy() {
        for (const circuitBreaker of this.circuitBreakers.values()) {
            circuitBreaker.destroy();
        }
        this.circuitBreakers.clear();
    }
}

/**
 * Request queue interceptor for managing concurrency and backpressure
 */
class RequestQueue {
    constructor(config) {
        this.config = config;
        this.activeRequests = 0;
        this.queue = [];
        this.totalProcessed = 0;
        this.totalRejected = 0;
        this.totalWaitTime = 0;
        this.destroyed = false;
    }
    async process(request, next, options) {
        if (this.destroyed) {
            throw new Error("Request queue destroyed");
        }
        // If we have capacity, process immediately
        if (this.activeRequests < this.config.maxConcurrentRequests) {
            this.activeRequests++;
            return this.executeRequest(request, next);
        }
        // Check if queue is full
        if (this.queue.length >= this.config.maxQueueSize) {
            this.totalRejected++;
            throw new Error("Request queue is full");
        }
        // Queue the request
        return new Promise((resolve, reject) => {
            const queuedRequest = {
                request,
                next,
                resolve,
                reject,
                queueTime: Date.now(),
                priority: options?.priority || "normal",
            };
            // Set timeout
            queuedRequest.timeout = setTimeout(() => {
                this.removeFromQueue(queuedRequest);
                reject(new Error("Request timeout"));
            }, this.config.requestTimeout);
            // Add to queue based on priority
            if (queuedRequest.priority === "high") {
                // Add to front of queue
                this.queue.unshift(queuedRequest);
            }
            else {
                // Add to end of queue
                this.queue.push(queuedRequest);
            }
        });
    }
    async executeRequest(_request, next) {
        try {
            const result = await next();
            this.totalProcessed++;
            return result;
        }
        finally {
            this.activeRequests--;
            // Use setTimeout to ensure metrics are consistent before processing next
            setTimeout(() => this.processNext(), 0);
        }
    }
    processNext() {
        if (this.queue.length === 0 ||
            this.activeRequests >= this.config.maxConcurrentRequests) {
            return;
        }
        const queuedRequest = this.queue.shift();
        // Clear timeout
        if (queuedRequest.timeout) {
            clearTimeout(queuedRequest.timeout);
        }
        // Calculate wait time
        const waitTime = Date.now() - queuedRequest.queueTime;
        this.totalWaitTime += waitTime;
        // Increment active requests before executing
        this.activeRequests++;
        // Execute the request
        this.executeRequest(queuedRequest.request, queuedRequest.next)
            .then(queuedRequest.resolve)
            .catch(queuedRequest.reject);
    }
    removeFromQueue(queuedRequest) {
        const index = this.queue.indexOf(queuedRequest);
        if (index >= 0) {
            this.queue.splice(index, 1);
        }
    }
    getMetrics() {
        const averageWaitTime = this.totalProcessed > 0 ? this.totalWaitTime / this.totalProcessed : 0;
        return {
            activeRequests: this.activeRequests,
            queuedRequests: this.queue.length,
            totalProcessed: this.totalProcessed,
            totalRejected: this.totalRejected,
            averageWaitTime,
        };
    }
    destroy() {
        this.destroyed = true;
        // Reject all queued requests
        for (const queuedRequest of this.queue) {
            if (queuedRequest.timeout) {
                clearTimeout(queuedRequest.timeout);
            }
            // Use setTimeout to avoid synchronous rejection during teardown
            setTimeout(() => {
                queuedRequest.reject(new Error("Request queue destroyed"));
            }, 0);
        }
        this.queue.length = 0;
    }
}
class RequestQueueInterceptor {
    constructor(config) {
        this.name = "RequestQueueInterceptor";
        this.order = 40; // Run after auth but before logging
        this.defaultConfig = {
            maxConcurrentRequests: 10,
            maxQueueSize: 50,
            requestTimeout: 30000,
        };
        this.queues = new Map();
        this.serviceConfigs = new Map();
        if (config) {
            this.defaultConfig = { ...this.defaultConfig, ...config };
        }
    }
    /**
     * Configure queue for a specific service
     */
    configureService(serviceName, config) {
        this.serviceConfigs.set(serviceName, { ...this.defaultConfig, ...config });
    }
    async intercept(direction, message, next, options) {
        // Only apply to outbound RPC calls
        if (direction !== "outbound" || !this.isRpcCall(message)) {
            return next();
        }
        const serviceName = message.service || "default";
        const queue = this.getQueue(serviceName);
        return queue.process(message, next, options);
    }
    getQueue(serviceName) {
        if (!this.queues.has(serviceName)) {
            const config = this.serviceConfigs.get(serviceName) || this.defaultConfig;
            const queue = new RequestQueue(config);
            this.queues.set(serviceName, queue);
        }
        return this.queues.get(serviceName);
    }
    isRpcCall(message) {
        return "kind" in message && message.kind === "call";
    }
    /**
     * Get metrics for a specific service queue
     */
    getMetrics(serviceName = "default") {
        const queue = this.queues.get(serviceName);
        if (!queue) {
            return {
                activeRequests: 0,
                queuedRequests: 0,
                totalProcessed: 0,
                totalRejected: 0,
                averageWaitTime: 0,
            };
        }
        return queue.getMetrics();
    }
    /**
     * Get metrics for all service queues
     */
    getAllMetrics() {
        const metrics = {};
        for (const [serviceName, queue] of this.queues) {
            metrics[serviceName] = queue.getMetrics();
        }
        return metrics;
    }
    /**
     * Cleanup resources
     */
    destroy() {
        for (const queue of this.queues.values()) {
            queue.destroy();
        }
        this.queues.clear();
    }
}

/**
 * Core protocol types for Bifrost RPC Framework
 */
/**
 * How long `initialize()` waits for the native handshake when the caller names no
 * deadline of its own.
 *
 * Sized to the host's own page deadline, not to how long a handshake usually takes.
 * The native host abandons the page and reports a timeout at 15s; a shorter bridge
 * deadline leaves a window where the shim can still arrive, the web side has already
 * rejected, and native is still waiting — so the handshake never completes even
 * though nothing is broken. Anything longer is wasted: by then the failure has
 * already been reported and seen.
 */
const DEFAULT_READY_TIMEOUT_MS = 15000;

/**
 * COMPAT SHIM — temporary, deleted with `src/compat`. See its README.
 *
 * ## The failure this prevents
 *
 * On affected STForestKit hosts (≤ forest-ios 5.23.0, older WebKit) the injected
 * console-bridge script replaces `console.log/debug/info/warn/error` with wrappers that
 * throw `ReferenceError` on every call: Annex B hoists the script's function
 * *declarations* out of the once-guard's `if` block but leaves its `const` bindings
 * behind, and reached through the hoisted binding those functions run with the global
 * environment, so `originalConsoleLogger` / `pendingNativeLogs` are unresolvable.
 *
 * From that moment **every `console.*` call in the document throws** — the host's own
 * scripts, this library, and the host app alike. Three consequences, all observed on a
 * physical iPhone 8 against the deployed staging site:
 *
 *   1. The RPC script dies on its first statement (a `console.log`) before defining
 *      `window.Bifrost`, so the native bridge is never created.
 *   2. This library throws from its own logging (9 call sites, and the host app enables
 *      debug logging), which surfaces as a `ReferenceError` rejecting `initialize()`.
 *   3. The host app's `catch` reports that `ReferenceError` to the user verbatim.
 *
 * (3) is why a global error listener cannot help: the throw is caught by application
 * code, so no `error` event is ever dispatched.
 *
 * ## The approach
 *
 * Install `console.log/debug/info/warn/error` as accessors at module load, before the
 * host injects. The getter always returns a guarded function; the setter records what
 * the host assigns instead of replacing the guard. The guard calls whatever the host
 * installed and, if that throws, falls back to the pristine method captured at load.
 *
 * So nothing in the document can ever throw from a `console` call, while a *healthy*
 * host's wrapper still runs and its console forwarding keeps working. That makes this
 * safe without version-gating: on a fixed host the guard is a pass-through.
 *
 * It also fixes the bridge at the source — with `console.log` no longer throwing, the
 * RPC script runs to completion on its **first** injection, so the readiness watchdog
 * never has to retry. `StForestKitOnceGuardRecovery` remains as a safety net for a host
 * whose `console` could not be guarded, or which fails for some other reason.
 */
/** Console methods the affected host replaces, and therefore the ones that can throw. */
const GUARDED_METHODS = ["log", "debug", "info", "warn", "error"];
/**
 * Pristine console methods, captured at module evaluation.
 *
 * Only as good as import ordering: the host injects on DOM-ready, so a bundle evaluated
 * before then — the normal case, and guaranteed for a dependency of the page's own
 * bundle — captures working functions. A bundle evaluated later would capture the
 * host's throwing wrappers, which is why the guard treats a throwing fallback as
 * "nothing more to try" rather than assuming the snapshot is good.
 */
const pristine = (() => {
    const snapshot = {};
    if (typeof console === "undefined") {
        return snapshot;
    }
    for (const name of GUARDED_METHODS) {
        const method = console[name];
        if (typeof method === "function") {
            snapshot[name] = method.bind(console);
        }
    }
    return snapshot;
})();
/** The pristine snapshot, for the recovery shim's own repair path. */
function pristineConsoleMethods() {
    return { ...pristine };
}
let installed = false;
/**
 * Make every `console.*` call non-throwing, permanently.
 *
 * Idempotent; a no-op when there is no `console`, and per-method tolerant of a
 * `console` whose properties cannot be redefined.
 */
function installStForestKitConsoleGuard() {
    if (installed || typeof console === "undefined") {
        return;
    }
    installed = true;
    for (const name of GUARDED_METHODS) {
        const fallback = pristine[name];
        if (!fallback) {
            continue;
        }
        // What the host most recently assigned. Starts as the pristine method so the guard
        // is a faithful pass-through until the host replaces it.
        let current = fallback;
        // Re-entrancy latch. Some code does `console.log = console.log.bind(console)`,
        // which would store a function that calls the guard again; without this the first
        // call would recurse until the stack blew. Depth is capped at one.
        let inFlight = false;
        const guarded = (...args) => {
            if (inFlight) {
                try {
                    fallback(...args);
                }
                catch {
                    /* nothing left to try */
                }
                return;
            }
            inFlight = true;
            try {
                current(...args);
            }
            catch {
                try {
                    fallback(...args);
                }
                catch {
                    /* nothing left to try */
                }
            }
            finally {
                inFlight = false;
            }
        };
        try {
            Object.defineProperty(console, name, {
                configurable: true,
                enumerable: true,
                get: () => guarded,
                set: (value) => {
                    if (typeof value === "function") {
                        current = value;
                    }
                },
            });
        }
        catch {
            // A `console` that refuses redefinition cannot be guarded. Leave it alone; the
            // recovery shim's repair path is the remaining line of defence.
        }
    }
}

/**
 * COMPAT SHIM — temporary, and designed to be deleted. See `src/compat/README.md`
 * for the removal steps and the conditions under which it becomes dead weight.
 *
 * ## What is broken
 *
 * STForestKit hosts up to and including forest-ios 5.23.0 wrap every injected plugin
 * script in a per-document once-guard that records the script's id *before* running it:
 *
 * ```js
 * if (!window.STForestKit.executedScripts.has(id)) {
 *     window.STForestKit.executedScripts.add(id);   // marked first
 *     <script>;                                     // a throw here leaves the mark
 * }
 * ```
 *
 * On WebKit the host's console-logging plugin installs wrappers that are entered
 * without the scope of the block that declared their dependencies, so calling any
 * `console` method throws `ReferenceError`. The `BifrostRPC` plugin script's first
 * statement inside its guard is a `console.log`, so it dies there — before defining
 * either `window.Bifrost` or `window.bifrostNativeBridge` — with its id already
 * marked. The host's readiness watchdog then re-injects every 500ms and every
 * attempt short-circuits on the guard, so the bridge stays dead for the lifetime of
 * the document and this library's handshake can never complete.
 *
 * Fixed host-side in stforestkit#446 (marks after the script runs, and the console
 * wrappers no longer depend on call-time lexical state). That fix cannot be applied
 * retroactively to installed binaries, which is why this shim exists.
 *
 * ## What this does
 *
 * Both halves of the broken state live on this page's own `window` — the host
 * evaluates its scripts in the main world — so the page can repair it:
 *
 *   1. put a callable `console` back, so the RPC script's first statement survives;
 *   2. clear *only* the `BifrostRPC` mark, and let the host's own watchdog re-inject.
 *
 * We deliberately do not inject anything ourselves. The host's watchdog is already
 * running for exactly as long as the page is un-ready, and its injected script is
 * idempotent by construction (`if (!window.Bifrost)` / `if (window.Bifrost &&
 * !window.bifrostNativeBridge)`), so unblocking it is both sufficient and the
 * smallest possible intervention.
 *
 * ## Why this cannot fire on a healthy host
 *
 * The precondition is `mark present AND bridge absent`, which is reachable only on an
 * affected host. A fixed host records the mark only after the script has run, so if
 * the mark is there the bridge is there too and we return before touching anything.
 * A host that has not injected yet has no mark. A non-STForestKit host has no
 * `window.STForestKit`. In all three cases this is a no-op.
 */
/** The host's plugin id for the script that installs the native bridge. */
const RPC_PLUGIN_ID = "BifrostRPC";
/**
 * Console methods the host's logging bridge replaces, and therefore the ones that
 * can be left throwing.
 */
const WRAPPED_METHODS = ["log", "debug", "info", "warn", "error"];
/**
 * Minimum spacing between attempts. The host watchdog re-injects on a 500ms tick, so
 * anything tighter would clear the mark again before the re-injection it is waiting
 * for has had a chance to run.
 */
const MIN_ATTEMPT_INTERVAL_MS = 750;
/**
 * Attempts allowed per document. A handful covers a re-injection that lands late or
 * throws once more; beyond that the failure is not the one this shim describes, and
 * repeatedly clearing the mark would keep the host re-running a script that has no
 * prospect of succeeding.
 */
const MAX_ATTEMPTS = 4;
const pristineConsole = pristineConsoleMethods();
// Installed at module load, before the host can replace `console`. This is the primary
// fix: with console calls unable to throw, the host's RPC script survives its opening
// `console.log` and installs the bridge on its first injection, and neither this library
// nor the host app can be thrown at from their own logging. Everything below is the
// remaining safety net for a host whose console could not be guarded.
installStForestKitConsoleGuard();
let attempts = 0;
let lastAttemptAt = 0;
/**
 * Whether calling `console.log` currently throws.
 *
 * Invoking it is the only way to find out — a poisoned wrapper is an ordinary callable
 * function and is indistinguishable by inspection. Reached only once the stuck
 * precondition already holds, so a healthy page never emits this.
 */
function consoleThrows() {
    try {
        // eslint-disable-next-line no-console -- probing callability is the point
        console.log();
        return false;
    }
    catch {
        return true;
    }
}
function assignConsole(methods) {
    for (const name of WRAPPED_METHODS) {
        const replacement = methods[name];
        if (replacement) {
            try {
                console[name] = replacement;
            }
            catch {
                // A host that made these non-writable is not one we can repair; the caller
                // still clears the mark, which is harmless, and the handshake still fails
                // on its own deadline rather than here.
            }
        }
    }
}
/**
 * Leave `console` callable.
 *
 * Restores the snapshot when it works, and neutralises to no-ops when it does not —
 * losing devtools output is a far smaller cost than leaving the RPC script unable to
 * execute its first statement.
 */
function repairConsole() {
    if (!consoleThrows()) {
        return "healthy";
    }
    assignConsole(pristineConsole);
    if (!consoleThrows()) {
        return "restored";
    }
    const noop = () => {
        /* nothing — better than throwing */
    };
    assignConsole({ log: noop, debug: noop, info: noop, warn: noop, error: noop });
    return "neutralised";
}
/**
 * Clear the host's stale `BifrostRPC` mark so its readiness watchdog can retry.
 *
 * Only these two ids are ever touched, and only `BifrostRPC` is cleared:
 *
 * - `ConsoleLoggingBridge` is left marked on purpose. Re-running it recreates the
 *   same broken lexical dependency and wraps an already-wrapped console. The cost is
 *   losing the host's console forwarding, which is host-side logging only.
 * - `MessageEventBridge` is left marked on purpose. On affected hosts its script does
 *   not remove a previously registered listener, so a re-injection stacks one and
 *   doubles every web→native message — duplicate analytics, duplicate URL opens.
 *   stforestkit#446's third change exists for precisely this reason.
 *
 * @returns true when a stale mark was cleared and a re-injection is now expected.
 */
function attemptStForestKitOnceGuardRecovery(now = Date.now()) {
    if (typeof window === "undefined") {
        return false;
    }
    // The bridge is present, so whatever happened the host got there. Nothing to fix,
    // and this is the branch that keeps the shim off every fixed host.
    if (window.bifrostNativeBridge) {
        return false;
    }
    const marks = window.STForestKit?.executedScripts;
    if (!marks || typeof marks.delete !== "function") {
        return false; // not an STForestKit host, or the guard is not set up yet
    }
    // No mark means the host has not run the script yet — it is not stuck, it is early.
    if (!marks.has(RPC_PLUGIN_ID)) {
        return false;
    }
    if (attempts >= MAX_ATTEMPTS || now - lastAttemptAt < MIN_ATTEMPT_INTERVAL_MS) {
        return false;
    }
    attempts += 1;
    lastAttemptAt = now;
    repairConsole();
    try {
        marks.delete(RPC_PLUGIN_ID);
    }
    catch {
        return false;
    }
    return true;
}

/**
 * NativeHandshake - owns the native-bridge handshake end to end.
 *
 * Split out of `BifrostBridge` because the handshake's invariants are about ordering,
 * lifetime and re-entrancy, and they only hold if they are enforced in one place.
 * Interleaved with RPC routing they had no locality: a caller could reach the state
 * that host code was concurrently mutating, and two owners scheduled evaluation.
 *
 * Everything about how a handshake progresses is hidden here -- which shim a send and
 * an ack belong to, how host re-entrancy is serialized, how the deadline and the
 * shared-watch memo interact, and when evaluation is scheduled. The RPC side only
 * awaits an outcome, forwards `native_ready`, and stops it on teardown.
 */
class NativeHandshake {
    /** Monotonic so ids never collide within a document; not security-relevant. */
    static nextHandshakeId() {
        NativeHandshake.handshakeCounter += 1;
        return `hs-${NativeHandshake.handshakeCounter}`;
    }
    constructor(host) {
        this.host = host;
    }
    /**
     * Resolve when the handshake completes, reject at `timeoutMs`.
     *
     * Overlapping callers share one attempt, so the shim is sent one `onWebReady()`
     * between them rather than one each.
     */
    async await(timeoutMs = DEFAULT_READY_TIMEOUT_MS) {
        const watch = this.bridgeWatch(timeoutMs);
        let timeoutTimer;
        const timeoutPromise = new Promise((_, reject) => {
            timeoutTimer = setTimeout(() => reject(new Error("Native bridge ready timeout")), timeoutMs);
        });
        try {
            // No `watch.stop()` on success: `bridgeWatch` registers its own retirement on
            // `settled` first, so it has always run by the time this resumes.
            await Promise.race([watch.settled, timeoutPromise]);
        }
        finally {
            clearTimeout(timeoutTimer);
        }
    }
    /** Route an inbound `native_ready` to the attempt in flight, if any. */
    handleNativeReady(message) {
        this.pendingWatch?.signalNativeReady(typeof message === "string"
            ? { bare: true }
            : { bare: false, handshakeId: message.handshakeId });
    }
    /**
     * Either the legacy bare string, or an envelope echoing back the id we passed to
     * `onWebReady()`. The echoing form is what lets an asynchronous ack be attributed
     * to the send it answers; a bare one only counts synchronously.
     */
    isNativeReady(message) {
        return (message === "native_ready" ||
            (typeof message === "object" &&
                message !== null &&
                message.type === "native_ready"));
    }
    /** Abandon any attempt in flight. */
    stop() {
        this.pendingWatch?.stop();
    }
    /**
     * Advance `watch` against whichever shim is on `window` right now, re-arming from
     * scratch if that is not the one the attempt in progress was bound to.
     *
     * Returns true once the bound shim has completed both halves of the handshake.
     */
    advanceHandshake(watch) {
        const shim = typeof window === "undefined" ? undefined : window.bifrostNativeBridge;
        if (shim !== watch.generation) {
            watch.generation = shim;
            watch.sent = false;
            watch.acked = false;
            // A fresh send needs a fresh id, so acks still owed to the previous shim
            // cannot answer it.
            watch.handshakeId = NativeHandshake.nextHandshakeId();
        }
        if (!shim) {
            // BEGIN stforestkit#469 compat — delete this block with `src/compat` (see its README).
            // The shim is missing, which on an affected host may mean its injection was
            // permanently skipped by a stale once-guard mark. Self-limiting: a no-op unless
            // that exact state is present.
            attemptStForestKitOnceGuardRecovery();
            // END stforestkit#469 compat
            return false;
        }
        if (!watch.sent) {
            if (typeof shim.onWebReady !== "function") {
                return false;
            }
            // Marked before the call, not after: the injected shim acks synchronously
            // from inside `onWebReady()`, so `signalNativeReady` re-enters here while the
            // call is still on the stack and would otherwise reject its own ack.
            watch.sent = true;
            watch.sending = true;
            try {
                // Hosts that echo the id back can be attributed exactly; the rest are
                // covered by the synchronous window this flag opens.
                shim.onWebReady(watch.handshakeId);
            }
            catch (error) {
                // A throw leaves the send unanswered, so roll the mark back and let the
                // next tick try the same shim again — otherwise a momentary host fault is
                // permanent unless the host happens to swap the shim out. An ack that
                // already landed inside the call still answers it, and survives.
                if (!watch.acked) {
                    watch.sent = false;
                    watch.handshakeId = NativeHandshake.nextHandshakeId();
                    throw error;
                }
            }
            finally {
                watch.sending = false;
            }
        }
        const ready = watch.acked || Boolean(shim.isReady);
        // Both `onWebReady()` and the `isReady` getter are host code and either can swap
        // the shim mid-pass. One check after both have run covers either seam: readiness
        // reported by an outgoing generation must not settle the incoming one, which has
        // been sent nothing and has reset neither half of the handshake.
        if (window.bifrostNativeBridge !== shim) {
            return false;
        }
        return ready;
    }
    /**
     * Poll the handshake until it completes, this bridge is destroyed, or the watch
     * reaches its deadline.
     *
     * The poll has to keep going for the caller's whole window, not just until the
     * shim first appears: the host injects it on DOM-ready and re-injects on a
     * watchdog, so on a slow page it can land seconds in. A deadline shorter than the
     * host's own leaves the page unable to hand shake even though the bridge is
     * present on `window` — indistinguishable downstream from a healthy bridge that
     * never delivered anything. See `DEFAULT_READY_TIMEOUT_MS`.
     */
    watchForNativeBridge(watch, settle) {
        // Nothing more should be attempted once any of these holds. Read as a function
        // rather than captured, because `advanceHandshake` runs host code that can flip
        // all three from underneath us.
        const terminal = () => this.host.isDestroyed() || watch.stopped || Date.now() > watch.deadline;
        // Exactly one future evaluation is ever queued, whether it was asked for by the
        // regular poll, by a drain that ran out of passes, or by an ack. Letting each
        // source keep its own timer meant a yielded drain and the poll both scheduled
        // successors, so every poll interval added another evaluation stream.
        let handle;
        let scheduledFor = Infinity;
        const schedule = (delayMs) => {
            const runAt = Date.now() + delayMs;
            if (handle !== undefined) {
                // Keeping the earliest is what stops a stream of poll-length reschedules
                // from pushing a yielded drain out indefinitely. Clearing before setting is
                // the part that keeps the count at one; no test separates the two, since
                // dropping this only lengthens a delay rather than adding a timer.
                if (runAt >= scheduledFor) {
                    return;
                }
                clearTimeout(handle);
            }
            scheduledFor = runAt;
            handle = setTimeout(() => {
                handle = undefined;
                scheduledFor = Infinity;
                run();
            }, delayMs);
        };
        /** One pass over the handshake, draining reruns requested from host code. */
        const evaluate = () => {
            if (watch.evaluating) {
                // Re-entered from host code. Ask for another pass instead of nesting: the
                // loop below drains the request once the current pass unwinds, so depth
                // stays at one however long a chain of self-replacing shims gets.
                watch.rerunRequested = true;
                return "pending";
            }
            watch.evaluating = true;
            try {
                for (let pass = 0; pass < NativeHandshake.MAX_EVALUATION_PASSES; pass++) {
                    watch.rerunRequested = false;
                    if (terminal()) {
                        return "pending";
                    }
                    let ready = false;
                    try {
                        ready = this.advanceHandshake(watch);
                    }
                    catch {
                        // `onWebReady` and the `isReady` getter are host code. A throwing shim
                        // is just a not-ready one; letting it escape would kill the poll loop
                        // while leaving the watch un-stopped in the shared memo, so every later
                        // caller inherits a handshake that can never complete.
                        ready = false;
                    }
                    // Re-checked after the fact: that same host code can destroy the bridge
                    // or stop this watch from inside the call, and a handshake completed
                    // against a bridge that no longer exists must not resolve anybody's
                    // `initialize()`.
                    if (terminal()) {
                        return "pending";
                    }
                    if (ready) {
                        settle();
                        return "ready";
                    }
                    if (!watch.rerunRequested) {
                        return "pending";
                    }
                }
                // The host is asking for passes without ever completing. Yield rather than
                // spinning here -- draining inline forever would block the page just as
                // surely as the recursion this loop replaced -- and let the caller queue the
                // continuation, so it does not become a second source of timers.
                return "yielded";
            }
            finally {
                watch.evaluating = false;
            }
        };
        const run = () => {
            const outcome = evaluate();
            if (outcome === "ready" || terminal()) {
                return;
            }
            schedule(outcome === "yielded" ? 0 : NativeHandshake.BRIDGE_POLL_INTERVAL_MS);
        };
        // An ack evaluates now and reschedules through the same single handle, so it
        // brings the next evaluation earlier rather than adding one.
        watch.evaluate = run;
        run();
    }
    /**
     * The in-flight handshake, shared by every `initialize()` call that overlaps it,
     * so overlapping callers send one `onWebReady()` between them rather than one
     * each when the shim lands.
     *
     * A watch is reused only while it is still running and its deadline still covers
     * the new caller's whole timeout window — otherwise the caller could outlive the
     * watcher it is relying on and miss a shim that arrives inside its own timeout.
     * `stop()` on handshake completion is what drops the memo, so polling never
     * continues past a successful handshake.
     */
    bridgeWatch(timeoutMs) {
        const now = Date.now();
        const required = now + timeoutMs;
        const existing = this.pendingWatch;
        // A live watch is extended, never replaced. Replacing it would strand any
        // caller already awaiting its promises on promises that can now never settle,
        // so that caller would reject at its own timeout even if the replacement
        // completed the handshake first. Extending also keeps the deadline covering
        // every live caller's window, which is what stops a caller from outliving the
        // watch it depends on.
        if (existing && !existing.stopped && now <= existing.deadline) {
            existing.deadline = Math.max(existing.deadline, required);
            return existing;
        }
        existing?.stop();
        // `settled` is a real deferred from the outset, never a placeholder swapped out
        // afterwards: the first poll tick runs synchronously below, and host code
        // reached from it can re-enter `initialize()` and be handed this same watch out
        // of the memo. A half-built watch carrying an already-resolved stand-in would
        // report a completed handshake to that caller.
        let settle;
        const settled = new Promise((resolve) => {
            settle = resolve;
        });
        const watch = {
            deadline: required,
            stopped: false,
            sent: false,
            acked: false,
            sending: false,
            evaluating: false,
            rerunRequested: false,
            handshakeId: NativeHandshake.nextHandshakeId(),
            settled,
            stop: () => {
                watch.stopped = true;
                if (this.pendingWatch === watch) {
                    this.pendingWatch = undefined;
                }
            },
            // An ack has to answer *this watch's current send*. Two ways to establish
            // that:
            //
            //  1. The host echoed back the id we passed to `onWebReady(handshakeId)`.
            //  2. The host sent the legacy *bare* `"native_ready"` while that call was
            //     still on the stack, so no other send could have produced it.
            //
            // The synchronous window is for the bare form only. An envelope carries an id
            // and is therefore always attributable, so one naming a different send is a
            // straggler however it arrives -- being delivered synchronously does not make
            // it answer the call it was not addressed to.
            //
            // An asynchronous bare ack is refused for the same reason: it cannot be told
            // apart from a replaced shim's straggler. Such hosts can still complete
            // through `shim.isReady`.
            signalNativeReady: (ack) => {
                // No separate "have we sent yet" check: a bare ack is refused unless the send
                // is on the stack, and an envelope can only echo an id the host was given by
                // a send, so neither form can be credited before one.
                const answersThisSend = ack.bare
                    ? watch.sending
                    : ack.handshakeId === watch.handshakeId;
                if (!answersThisSend) {
                    return;
                }
                watch.acked = true;
                // A tick may be up to a poll interval away and the caller's deadline can
                // fall inside that gap, so a valid ack is acted on now rather than merely
                // recorded. Safe to call unconditionally: `evaluate` serializes, so an ack
                // raised from inside host code queues a further pass instead of recursing.
                watch.evaluate?.();
            },
        };
        // Retiring the memo cannot depend on a caller observing the success: the
        // `initialize()` that owned the await may have already rejected on its own
        // timeout, leaving the watch to complete with nobody watching. A fulfilled
        // watch must not then be handed to a later caller as if it were current.
        void settled.then(() => {
            watch.stop();
            this.host.onCompleted();
        });
        // Published before polling starts, not after: the first tick runs synchronously
        // and the injected shim acks synchronously from inside `onWebReady()`, so
        // `handleGlobalMessage` re-enters while this is still being built. Assigning
        // afterwards would leave `pendingWatch` unset for exactly that ack.
        this.pendingWatch = watch;
        this.watchForNativeBridge(watch, settle);
        return watch;
    }
}
NativeHandshake.handshakeCounter = 0;
/**
 * Upper bound on passes drained in one synchronous evaluation. Reached only by a
 * host that keeps asking for another pass without ever completing the handshake.
 */
NativeHandshake.MAX_EVALUATION_PASSES = 64;
/** Interval between native-bridge presence checks. */
NativeHandshake.BRIDGE_POLL_INTERVAL_MS = 100;

/**
 * BifrostBridge - Singleton that manages native bridge communication
 *
 * This class handles the actual WebView bridge connection and coordinates
 * message passing between multiple BifrostWeb client instances.
 */
/**
 * Singleton bridge that manages communication with the native side
 */
class BifrostBridge {
    constructor() {
        this.clients = new Map();
        this.pendingCalls = new Map();
        this.handlerInitialized = false;
        this.isDestroyed = false;
        /**
         * Owns the handshake outright: which shim a send and an ack belong to, host
         * re-entrancy, the deadline, the shared attempt, and when evaluation is scheduled.
         * This class only awaits an outcome, forwards `native_ready`, and stops it.
         */
        this.handshake = new NativeHandshake({
            isDestroyed: () => this.isDestroyed,
            onCompleted: () => this.markClientsReady(),
        });
        this.setupGlobalHandler();
    }
    /**
     * Get the singleton instance
     */
    static getInstance() {
        if (!BifrostBridge.instance || BifrostBridge.instance.isDestroyed) {
            BifrostBridge.instance = new BifrostBridge();
        }
        return BifrostBridge.instance;
    }
    /**
     * Initialize the bridge connection
     */
    async initialize(readyTimeout = DEFAULT_READY_TIMEOUT_MS) {
        if (this.isDestroyed) {
            throw new Error("BifrostBridge has been destroyed");
        }
        // Wait for native bridge to be ready
        await this.handshake.await(readyTimeout);
    }
    /**
     * Register a client with the bridge
     */
    registerClient(client) {
        if (this.isDestroyed) {
            throw new Error("BifrostBridge has been destroyed");
        }
        this.clients.set(client.clientId, client);
    }
    /**
     * Unregister a client from the bridge
     */
    unregisterClient(clientId) {
        // Remove client
        this.clients.delete(clientId);
        // Cancel any pending calls for this client
        for (const [callId, pendingCall] of this.pendingCalls) {
            if (pendingCall.clientId === clientId) {
                clearTimeout(pendingCall.timeout);
                pendingCall.reject(new Error("Client instance destroyed"));
                this.pendingCalls.delete(callId);
            }
        }
        // If no more clients, cleanup global handler
        if (this.clients.size === 0) {
            this.cleanup();
        }
    }
    /**
     * Send an RPC call through the bridge
     */
    async sendCall(clientId, callId, service, method, payload, options, envelope) {
        if (this.isDestroyed) {
            throw new Error("BifrostBridge has been destroyed");
        }
        // Use provided envelope or create a new one
        const finalEnvelope = envelope || {
            id: callId,
            kind: "call",
            service,
            method,
            payload,
            ts: Date.now(),
        };
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingCalls.delete(callId);
                reject(new Error("RPC call timeout"));
            }, options?.timeout ?? 5000);
            this.pendingCalls.set(callId, {
                resolve,
                reject,
                timeout,
                clientId,
            });
            this.sendToNative(finalEnvelope);
        });
    }
    /**
     * Send a notification through the bridge
     */
    sendNotification(service, method, payload, callId) {
        if (this.isDestroyed) {
            return;
        }
        const envelope = {
            id: callId,
            kind: "notify",
            service,
            method,
            payload,
            ts: Date.now(),
        };
        this.sendToNative(envelope);
    }
    /**
     * Send a response back to native
     */
    sendResponse(response) {
        if (this.isDestroyed) {
            return;
        }
        this.sendToNative(response);
    }
    /**
     * Setup global message handler
     */
    setupGlobalHandler() {
        if (this.handlerInitialized || typeof window === "undefined") {
            return;
        }
        window.bifrostHandleN2W = (message) => {
            this.handleGlobalMessage(message);
        };
        this.handlerInitialized = true;
    }
    /**
     * Handle incoming messages from native
     */
    async handleGlobalMessage(message) {
        if (this.isDestroyed) {
            return;
        }
        try {
            if (this.isResponse(message)) {
                this.handleResponse(message);
            }
            else if (this.isCall(message)) {
                await this.handleIncomingCall(message);
            }
            else if (this.isNotification(message)) {
                this.handleIncomingNotification(message);
            }
            else if (this.handshake.isNativeReady(message)) {
                this.handshake.handleNativeReady(message);
            }
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error("[BifrostBridge] Error handling global message:", error);
        }
    }
    /**
     * Handle response messages
     */
    handleResponse(response) {
        const pendingCall = this.pendingCalls.get(response.id);
        if (!pendingCall) {
            return; // Response for unknown call, might be timeout
        }
        this.pendingCalls.delete(response.id);
        clearTimeout(pendingCall.timeout);
        if (response.error) {
            const error = new RpcCallError(response.error);
            pendingCall.reject(error);
        }
        else {
            pendingCall.resolve(response.result);
        }
    }
    /**
     * Handle incoming calls from native
     */
    async handleIncomingCall(envelope) {
        // Broadcast to all active clients (original behavior)
        for (const client of this.clients.values()) {
            if (!client.isDestroyed) {
                try {
                    await client.handleIncomingCall(envelope);
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("[BifrostBridge] Error routing call to client:", error);
                }
            }
        }
    }
    /**
     * Handle incoming notifications from native
     */
    handleIncomingNotification(envelope) {
        // Broadcast notifications to all active clients
        for (const client of this.clients.values()) {
            if (!client.isDestroyed) {
                try {
                    client.handleIncomingNotification(envelope);
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("[BifrostBridge] Error routing notification to client:", error);
                }
            }
        }
    }
    /**
     * Send message to native bridge
     */
    sendToNative(message) {
        if (!window.bifrostNativeBridge) {
            throw new Error("Native bridge not available");
        }
        window.bifrostNativeBridge.sendToNative(message);
    }
    /**
     * Tell every registered client the handshake landed. `initialize()` resolving is
     * not a reliable trigger for this: the caller that started the watch may have
     * rejected on its own timeout long before a later caller's extension carried it
     * to completion.
     */
    markClientsReady() {
        for (const client of this.clients.values()) {
            try {
                client.markBridgeReady?.();
            }
            catch (error) {
                // Client code. One faulting client must not stop the rest from learning
                // the bridge is usable.
                // eslint-disable-next-line no-console
                console.error("[BifrostBridge] markBridgeReady threw:", error);
            }
        }
    }
    /**
     * Cleanup resources
     */
    cleanup() {
        // Cancel all pending calls
        for (const [, pendingCall] of this.pendingCalls) {
            clearTimeout(pendingCall.timeout);
            pendingCall.reject(new Error("BifrostBridge destroyed"));
        }
        this.pendingCalls.clear();
        // Clear clients
        this.clients.clear();
        // Remove global handler
        if (typeof window !== "undefined") {
            window.bifrostHandleN2W = undefined;
            this.handlerInitialized = false;
        }
        this.handshake.stop();
        this.isDestroyed = true;
    }
    /**
     * Reset bridge for testing (package private)
     */
    static resetForTesting() {
        // @ts-ignore - Reset singleton for testing
        BifrostBridge.instance = undefined;
    }
    /**
     * Destroy the bridge (for testing purposes)
     */
    static destroy() {
        if (BifrostBridge.instance) {
            BifrostBridge.instance.cleanup();
            // @ts-ignore - Reset singleton for testing
            BifrostBridge.instance = undefined;
        }
    }
    /**
     * Check if message is a response
     */
    isResponse(message) {
        return (message &&
            typeof message.id === "string" &&
            (message.result !== undefined || message.error !== undefined));
    }
    /**
     * Check if message is a call
     */
    isCall(message) {
        return message && message.kind === "call" && message.service;
    }
    /**
     * Check if message is a notification
     */
    isNotification(message) {
        return message && message.kind === "notify";
    }
    /**
     * Check if message is a native ready
     */
    /**
     * Get bridge status (for debugging/testing)
     */
    getStatus() {
        return {
            clientCount: this.clients.size,
            pendingCallCount: this.pendingCalls.size,
            isDestroyed: this.isDestroyed,
            handlerInitialized: this.handlerInitialized,
        };
    }
}

/**
 * ProxyBuilder - Fluent API for configuring per-method CallOptions
 *
 * Provides a builder pattern for creating native proxies with method-specific
 * call options, enabling fine-grained control over timeouts and retry strategies.
 *
 * @example
 * ```typescript
 * const userService = bifrost
 *   .createProxyBuilder<UserService>('UserService')
 *   .withDefaultOptions({ timeout: 5000, maxRetries: 2 })
 *   .configureMethod('generateReport', { timeout: 60000, maxRetries: 1 })
 *   .configureMethod('quickCheck', { timeout: 1000, maxRetries: 0 })
 *   .build();
 * ```
 */
/**
 * ProxyBuilder provides a fluent API for configuring native proxies with
 * per-method call options.
 *
 * Supports four-tier option precedence:
 * - Global (BifrostConfig.defaultCallOptions)
 * - Proxy (ProxyBuilder.withDefaultOptions)
 * - Method (ProxyBuilder.configureMethod)
 * - Call (per-call options parameter)
 *
 * Priority: global < proxy < method < call (rightmost wins)
 *
 * @template T - The service interface type
 *
 * @example
 * ```typescript
 * interface UserService {
 *   getProfile(userId: number): Promise<UserProfile>;
 *   generateReport(params: ReportParams): Promise<Report>;
 * }
 *
 * const userService = bifrost
 *   .createProxyBuilder<UserService>('UserService')
 *   .withDefaultOptions({ timeout: 5000 })
 *   .configureMethod('generateReport', { timeout: 60000 })
 *   .build();
 * ```
 */
class ProxyBuilder {
    /**
     * Creates a new ProxyBuilder instance.
     *
     * @param bifrostWeb - The BifrostWeb instance to create the proxy from
     * @param serviceName - Name of the service to create a proxy for
     *
     * @throws Error if service name is invalid
     */
    constructor(bifrostWeb, serviceName) {
        this.bifrostWeb = bifrostWeb;
        this.isBuilt = false;
        this.validateServiceName(serviceName);
        this.config = {
            serviceName,
            methodOptions: {},
            proxyOptions: {},
        };
    }
    /**
     * Set default options for all methods on this proxy.
     *
     * These options override global defaults but can be overridden by
     * method-specific configuration or call-level options.
     *
     * Can be called multiple times; last call wins.
     *
     * @param options - Default call options for all methods
     * @returns This builder instance for method chaining
     *
     * @example
     * ```typescript
     * builder.withDefaultOptions({ timeout: 5000, maxRetries: 2 });
     * ```
     */
    withDefaultOptions(options) {
        this.throwIfBuilt();
        this.validateOptions(options);
        this.config.defaultOptions = options;
        return this;
    }
    /**
     * Configure call options for a specific method.
     *
     * These options override proxy defaults but can be overridden at call time.
     *
     * Can be called multiple times for the same method; last call wins.
     *
     * @param methodName - Name of the method to configure
     * @param options - Call options for this method
     * @returns This builder instance for method chaining
     *
     * @throws Error if method name is invalid
     * @throws Error if options are invalid
     *
     * @example
     * ```typescript
     * builder
     *   .configureMethod('slowMethod', { timeout: 30000, maxRetries: 1 })
     *   .configureMethod('quickMethod', { timeout: 1000, maxRetries: 0 });
     * ```
     */
    configureMethod(methodName, options) {
        this.throwIfBuilt();
        this.validateMethodName(methodName);
        this.validateOptions(options);
        this.config.methodOptions[methodName] = options;
        return this;
    }
    /**
     * Build the configured proxy.
     *
     * Can only be called once per builder instance. After building, the builder
     * is locked and cannot be modified.
     *
     * @returns A typed proxy for the service with configured options
     *
     * @throws Error if builder has already been built
     *
     * @example
     * ```typescript
     * const service = builder
     *   .withDefaultOptions({ timeout: 5000 })
     *   .configureMethod('slowMethod', { timeout: 30000 })
     *   .build();
     * ```
     */
    build() {
        this.throwIfBuilt();
        this.isBuilt = true;
        // Prepare proxy options with method-specific configuration
        const proxyOptions = {
            ...this.config.proxyOptions,
            defaultCallOptions: this.config.defaultOptions,
            methodOptions: this.config.methodOptions,
        };
        // Create proxy using NativeProxyManager
        return this.bifrostWeb.createNativeProxy(this.config.serviceName, proxyOptions);
    }
    /**
     * Get the current configuration for debugging purposes.
     *
     * Returns a readonly copy of the builder's configuration state.
     *
     * @returns Readonly configuration object
     *
     * @example
     * ```typescript
     * const config = builder.getConfiguration();
     * console.log('Service:', config.serviceName);
     * console.log('Default options:', config.defaultOptions);
     * console.log('Method options:', config.methodOptions);
     * ```
     */
    getConfiguration() {
        return {
            serviceName: this.config.serviceName,
            defaultOptions: this.config.defaultOptions,
            methodOptions: { ...this.config.methodOptions },
            proxyOptions: { ...this.config.proxyOptions },
        };
    }
    /**
     * Reset the builder configuration to initial state.
     *
     * Clears all default options and method-specific configurations.
     * Does not reset the service name.
     *
     * @returns This builder instance for method chaining
     *
     * @throws Error if builder has already been built
     *
     * @example
     * ```typescript
     * builder
     *   .withDefaultOptions({ timeout: 5000 })
     *   .configureMethod('slowMethod', { timeout: 30000 })
     *   .reset()  // Clear all configuration
     *   .withDefaultOptions({ timeout: 3000 });
     * ```
     */
    reset() {
        this.throwIfBuilt();
        this.config.defaultOptions = undefined;
        this.config.methodOptions = {};
        return this;
    }
    /**
     * Validate service name format.
     *
     * Service names must:
     * - Not be empty
     * - Start with a letter
     * - Contain only letters, numbers, and underscores
     *
     * @param serviceName - Service name to validate
     * @throws Error if service name is invalid
     */
    validateServiceName(serviceName) {
        if (!serviceName || typeof serviceName !== "string") {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
        const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
        if (!validNamePattern.test(serviceName)) {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
    }
    /**
     * Validate method name format.
     *
     * Method names must:
     * - Not be empty
     * - Start with a letter
     * - Contain only letters, numbers, and underscores
     *
     * @param methodName - Method name to validate
     * @throws Error if method name is invalid
     */
    validateMethodName(methodName) {
        if (!methodName || typeof methodName !== "string") {
            throw new Error(`Invalid method name: ${methodName}`);
        }
        const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
        if (!validNamePattern.test(methodName)) {
            throw new Error(`Invalid method name: ${methodName}`);
        }
    }
    /**
     * Validate CallOptions structure.
     *
     * Ensures that:
     * - timeout is positive if specified
     * - maxRetries is non-negative if specified
     *
     * @param options - Options to validate
     * @throws Error if options are invalid
     */
    validateOptions(options) {
        if (options.timeout !== undefined) {
            if (typeof options.timeout !== "number" || options.timeout < 0) {
                throw new Error(`Invalid timeout: ${options.timeout}. Must be a positive number.`);
            }
        }
        if (options.maxRetries !== undefined) {
            if (typeof options.maxRetries !== "number" || options.maxRetries < 0) {
                throw new Error(`Invalid maxRetries: ${options.maxRetries}. Must be a non-negative number.`);
            }
        }
    }
    /**
     * Throw error if builder has already been built.
     *
     * @throws Error if builder has already been built
     */
    throwIfBuilt() {
        if (this.isBuilt) {
            throw new Error("Builder has already been built. Create a new builder to configure another proxy.");
        }
    }
}

/**
 * BifrostWeb - Main RPC framework class
 * TypeScript-first bidirectional WebView RPC framework
 */
/**
 * BifrostWeb - Main RPC framework class for bidirectional WebView communication
 *
 * Enables seamless communication between web content and native applications
 * with full TypeScript support, error handling, and advanced features like
 * interceptors, and connection management.
 *
 * @example
 * ```typescript
 * const bifrost = new BifrostWeb({
 *   readyTimeout: 15000,
 *   maxInflightCalls: 32
 * });
 *
 * await bifrost.initialize();
 * const result = await bifrost.call('UserService', 'getProfile', { userId: 123 });
 * ```
 */
class BifrostWeb {
    /**
     * Creates a new BifrostWeb instance
     *
     * @param config - Configuration options for the RPC framework
     * @param config.readyTimeout - Deadline for the native handshake. Defaults to
     * DEFAULT_READY_TIMEOUT_MS, which is sized to the host's own page deadline.
     * @param config.maxInflightCalls - Maximum concurrent RPC calls (default: 32)
     * @param config.retryConfig - Retry configuration for failed calls
     * @param config.enableDebug - Enable debug logging (default: false)
     * @param config.interceptors - Default interceptors to register
     * @param config.onReady - Callback when native bridge is ready
     * @param config.onError - Callback for error handling
     */
    constructor(config = {}) {
        this.callIdCounter = 0;
        this.isDestroyed = false;
        // Generate unique client ID
        this.clientId = this.generateClientId();
        // Set default configuration
        this.config = {
            readyTimeout: config.readyTimeout ?? DEFAULT_READY_TIMEOUT_MS,
            maxInflightCalls: config.maxInflightCalls ?? 32,
            retryConfig: config.retryConfig ?? {
                maxRetries: 3,
                retryDelay: 1000,
                retryBackoff: 2
            },
            enableDebug: config.enableDebug ?? false,
            onReady: config.onReady,
            onError: config.onError
        };
        // Store global default call options
        this.defaultCallOptions = config.defaultCallOptions;
        // Initialize core components
        this.connectionState = new ConnectionState(this.config.readyTimeout);
        this.retryManager = new RetryManager({
            maxRetries: this.config.retryConfig?.maxRetries ?? 3,
            retryDelay: this.config.retryConfig?.retryDelay ?? 1000,
            retryBackoff: this.config.retryConfig?.retryBackoff ?? 2
        });
        this.serviceRegistry = new ServiceRegistry();
        this.proxyManager = new NativeProxyManager(this);
        this.interceptorChain = new InterceptorChain();
        // Register default interceptors from config
        if (config.interceptors) {
            for (const interceptor of config.interceptors) {
                this.interceptorChain.addInterceptor(interceptor);
            }
        }
    }
    /**
     * Get fresh bridge instance (handles reconnection after destruction)
     */
    getBridge() {
        return BifrostBridge.getInstance();
    }
    /**
     * Initialize the BifrostWeb framework
     *
     * Sets up the connection to the native bridge, configures message handlers,
     * and waits for the native side to be ready for communication.
     *
     * @throws {Error} If native bridge is not available or ready timeout is exceeded
     *
     * @example
     * ```typescript
     * const bifrost = new BifrostWeb();
     * await bifrost.initialize();
     * // Now ready to make RPC calls
     * ```
     */
    async initialize() {
        if (this.isDestroyed) {
            throw new Error('BifrostWeb has been destroyed');
        }
        // Get bridge and register this client
        const bridge = this.getBridge();
        bridge.registerClient(this);
        // Initialize the bridge (handles native bridge setup and global handler)
        await bridge.initialize(this.config.readyTimeout);
        this.connectionState.setReady();
    }
    /**
     * The shared bridge handshake completed (see IBifrostClient.markBridgeReady).
     */
    markBridgeReady() {
        if (!this.isDestroyed) {
            this.connectionState.setReady();
        }
    }
    /**
     * Register a service for native-to-web calls
     *
     * Registers a JavaScript object as a service that can be called from the native side.
     * All methods on the service object become callable via RPC.
     *
     * @param serviceName - Unique name for the service
     * @param serviceImpl - Object containing methods to expose to native side
     *
     * @throws {Error} If service name is already registered or invalid
     *
     * @example
     * ```typescript
     * const webService = {
     *   showNotification: (message: string) => {
     *     new Notification(message);
     *     return { displayed: true };
     *   },
     *   validateForm: async (data: any) => {
     *     return { valid: true, errors: [] };
     *   }
     * };
     *
     * bifrost.registerService('WebUIService', webService);
     * ```
     */
    registerService(serviceName, serviceImpl) {
        this.throwIfDestroyed();
        this.serviceRegistry.register(serviceName, serviceImpl);
        // Keep the simple broadcast behavior for now - no need to register with bridge
    }
    /**
     * Unregister a service
     */
    unregisterService(serviceName) {
        this.throwIfDestroyed();
        this.serviceRegistry.unregister(serviceName);
    }
    /**
     * Check if this client has a specific service (required by IBifrostClient)
     */
    hasService(serviceName) {
        return this.serviceRegistry.listServices().includes(serviceName);
    }
    /**
     * Call a native method with automatic retry logic
     *
     * Sends an RPC call to the native side and waits for the response.
     * Includes built-in retry logic for failed calls and timeout handling.
     *
     * @param service - Name of the native service to call
     * @param method - Method name on the service
     * @param payload - Parameters to send with the call (default: {})
     * @param options - Call options including timeout and retries
     * @returns Promise that resolves with the method result
     *
     * @throws {RpcCallError} If the call fails or times out
     *
     * @example
     * ```typescript
     * // Simple call
     * const result = await bifrost.call('UserService', 'getProfile', { userId: 123 });
     *
     * // Call with options
     * const result = await bifrost.call('FileService', 'upload', { name: 'file.pdf' }, {
     *   timeout: 30000,
     * });
     * ```
     */
    async call(service, method, payload = {}, options) {
        this.throwIfDestroyed();
        // Merge global + call-level options (no proxy/method involved in direct calls)
        const finalOptions = this.mergeCallOptions(undefined, undefined, options);
        return this.retryManager.withRetry(async () => {
            return this.performCall(service, method, payload, finalOptions);
        });
    }
    /**
     * Send notification to native (fire-and-forget)
     */
    notify(service, method, payload = {}) {
        this.throwIfDestroyed();
        const callId = this.generateCallId();
        const envelope = {
            id: callId,
            kind: "notify",
            service,
            method,
            payload,
            ts: Date.now()
        };
        // Process through outbound interceptor chain (async but not awaited for fire-and-forget)
        this.interceptorChain.process('outbound', envelope, async () => {
            this.getBridge().sendNotification(service, method, payload, callId);
            return undefined;
        }).catch((error) => {
            // Log interceptor errors but don't throw since notify is fire-and-forget
            if (this.config.enableDebug) {
                console.error('Interceptor error in notify:', error); // eslint-disable-line no-console
            }
        });
    }
    /**
     * Create a typed native service proxy with caching and validation
     *
     * Creates a strongly-typed proxy object that provides direct method access
     * to native services. Method calls are automatically converted to RPC calls
     * with full TypeScript support and IntelliSense.
     *
     * @template T - TypeScript interface defining the service methods
     * @param serviceName - Name of the native service to proxy
     * @param options - Proxy configuration options
     * @returns Typed proxy object with methods that call the native service
     *
     * @example
     * ```typescript
     * interface UserService {
     *   getProfile(userId: number): Promise<UserProfile>;
     *   updateProfile(data: Partial<UserProfile>): Promise<void>;
     * }
     *
     * const userService = bifrost.createNativeProxy<UserService>('UserService');
     * const profile = await userService.getProfile(123);
     * await userService.updateProfile({ name: 'New Name' });
     * ```
     */
    createNativeProxy(serviceName, options) {
        this.throwIfDestroyed();
        return this.proxyManager.createProxy(serviceName, options);
    }
    /**
     * Create a ProxyBuilder for configuring per-method call options
     *
     * Provides a fluent API for configuring different CallOptions (timeout, maxRetries)
     * for individual methods on a proxy. Supports four-tier option precedence:
     * global < proxy < method < call.
     *
     * @template T - TypeScript interface defining the service methods
     * @param serviceName - Name of the native service to proxy
     * @returns ProxyBuilder instance for configuring the proxy
     *
     * @example
     * ```typescript
     * interface UserService {
     *   quickCheck(): Promise<boolean>;
     *   generateReport(params: any): Promise<Report>;
     * }
     *
     * const userService = bifrost
     *   .createProxyBuilder<UserService>('UserService')
     *   .withDefaultOptions({ timeout: 5000 })
     *   .configureMethod('generateReport', { timeout: 60000, maxRetries: 1 })
     *   .build();
     *
     * const isReady = await userService.quickCheck(); // Uses 5000ms timeout
     * const report = await userService.generateReport({}); // Uses 60000ms timeout
     * ```
     */
    createProxyBuilder(serviceName) {
        this.throwIfDestroyed();
        return new ProxyBuilder(this, serviceName);
    }
    /**
     * Create a native service proxy (legacy method for backward compatibility)
     */
    createLegacyNativeProxy(serviceName) {
        this.throwIfDestroyed();
        return {
            call: (method, payload, options) => {
                return this.call(serviceName, method, payload, options);
            },
            notify: (method, payload) => {
                this.notify(serviceName, method, payload);
            }
        };
    }
    /**
     * List all registered services
     */
    listServices() {
        this.throwIfDestroyed();
        return this.serviceRegistry.listServices();
    }
    /**
     * Get service descriptor
     */
    getServiceDescriptor(serviceName) {
        this.throwIfDestroyed();
        return this.serviceRegistry.getServiceDescriptor(serviceName);
    }
    /**
     * List all cached native proxies
     */
    listCachedProxies() {
        this.throwIfDestroyed();
        return this.proxyManager.listCachedProxies();
    }
    /**
     * Clear specific proxy from cache
     */
    clearProxyCache(serviceName) {
        this.throwIfDestroyed();
        this.proxyManager.clearProxy(serviceName);
    }
    /**
     * Clear all proxy caches
     */
    clearAllProxyCaches() {
        this.throwIfDestroyed();
        this.proxyManager.clearAllProxies();
    }
    /**
     * Add an interceptor to the chain
     *
     * Interceptors are executed in order based on their `order` property.
     * Lower order values execute first.
     *
     * @param interceptor - The interceptor to add
     *
     * @example
     * ```typescript
     * import { LoggingInterceptor } from '@seekrtech/bifrost-web';
     *
     * const loggingInterceptor = new LoggingInterceptor();
     * bifrost.addInterceptor(loggingInterceptor);
     * ```
     */
    addInterceptor(interceptor) {
        this.throwIfDestroyed();
        this.interceptorChain.addInterceptor(interceptor);
    }
    /**
     * Remove an interceptor from the chain by name
     *
     * @param name - Name of the interceptor to remove
     *
     * @example
     * ```typescript
     * bifrost.removeInterceptor('LoggingInterceptor');
     * ```
     */
    removeInterceptor(name) {
        this.throwIfDestroyed();
        this.interceptorChain.removeInterceptor(name);
    }
    /**
     * Clear all interceptors from the chain
     */
    clearInterceptors() {
        this.throwIfDestroyed();
        this.interceptorChain.clearInterceptors();
    }
    /**
     * Get all registered interceptors
     */
    getInterceptors() {
        this.throwIfDestroyed();
        return this.interceptorChain.getInterceptors();
    }
    /**
     * Get global default call options (used by NativeProxyManager)
     *
     * @returns Global default CallOptions or undefined
     */
    getDefaultCallOptions() {
        return this.defaultCallOptions;
    }
    /**
     * Merge call options from multiple levels
     * Priority: global < proxy < method < call (rightmost wins)
     *
     * @param proxyOptions - Proxy-level default options
     * @param methodOptions - Method-specific options
     * @param callOptions - Call-level options
     * @returns Merged CallOptions or undefined if all inputs are undefined
     */
    mergeCallOptions(proxyOptions, methodOptions, callOptions) {
        const merged = {
            ...this.defaultCallOptions,
            ...proxyOptions,
            ...methodOptions,
            ...callOptions
        };
        // Return undefined if result is empty object (preserves backward compatibility)
        return Object.keys(merged).length === 0 ? undefined : merged;
    }
    /**
     * Destroy the framework and cleanup resources
     */
    destroy() {
        if (this.isDestroyed) {
            return;
        }
        // Unregister from bridge (if bridge still exists)
        try {
            this.getBridge().unregisterClient(this.clientId);
        }
        catch (error) {
            // Bridge might be destroyed already, that's okay
        }
        // Cleanup proxy manager
        this.proxyManager.destroy();
        this.isDestroyed = true;
    }
    /**
     * Perform actual RPC call
     */
    async performCall(service, method, payload, options) {
        await this.connectionState.ensureReady();
        const callId = this.generateCallId();
        const envelope = {
            id: callId,
            kind: "call",
            service,
            method,
            payload,
            ts: Date.now()
        };
        // Process through outbound interceptor chain
        return this.interceptorChain.process('outbound', envelope, async () => {
            // Use bridge to send call with the (potentially modified) envelope
            return this.getBridge().sendCall(this.clientId, envelope.id, envelope.service, envelope.method, envelope.payload, options, envelope);
        });
    }
    /**
     * Handle incoming call from native (required by IBifrostClient)
     */
    async handleIncomingCall(envelope) {
        // Process through inbound interceptor chain
        await this.interceptorChain.process('inbound', envelope, async () => {
            let result = null;
            let error = null;
            try {
                result = await this.serviceRegistry.invoke(envelope.service, envelope.method, envelope.payload);
            }
            catch (err) {
                if (err instanceof RpcCallError) {
                    error = {
                        code: err.code,
                        message: err.message
                    };
                }
                else {
                    error = {
                        code: ErrorCode.INTERNAL_ERROR,
                        message: err instanceof Error ? err.message : 'Unknown error'
                    };
                }
            }
            // Create response
            const response = {
                id: envelope.id,
                ok: error === null,
                result,
                error,
                ts: Date.now()
            };
            // Process response through outbound interceptor chain
            await this.interceptorChain.process('outbound', response, async () => {
                // Use bridge to send response back
                this.getBridge().sendResponse(response);
                return undefined;
            });
            return undefined;
        });
    }
    /**
     * Handle incoming notification from native (required by IBifrostClient)
     */
    handleIncomingNotification(envelope) {
        // Process through inbound interceptor chain (async but not awaited for fire-and-forget)
        this.interceptorChain.process('inbound', envelope, async () => {
            await this.serviceRegistry.invoke(envelope.service, envelope.method, envelope.payload);
        }).catch((error) => {
            // Log interceptor errors but don't throw since notifications are fire-and-forget
            if (this.config.enableDebug) {
                console.error('Interceptor error in handleIncomingNotification:', error); // eslint-disable-line no-console
            }
        });
    }
    /**
     * Generate unique client ID
     */
    generateClientId() {
        return `client_${Math.random().toString(36).substring(2)}_${Date.now()}`;
    }
    /**
     * Generate unique call ID
     */
    generateCallId() {
        return `${this.clientId}_${++this.callIdCounter}_${Date.now()}`;
    }
    /**
     * Throw error if framework has been destroyed
     */
    throwIfDestroyed() {
        if (this.isDestroyed) {
            throw new Error('BifrostWeb has been destroyed');
        }
    }
}

export { AuthInterceptor, BifrostWeb, CircuitBreakerInterceptor, CircuitBreakerState, ConnectionState, ErrorCategory, ErrorCode, InterceptorChain, LogLevel, LoggingInterceptor, NativeProxyManager, RequestInterceptor, RequestQueueInterceptor, ResponseInterceptor, RetryManager, RpcCallError, ServiceRegistry, ServiceValidator, TimingInterceptor, ValidationInterceptor };
//# sourceMappingURL=index.esm.js.map
